export const ANDROID_APP_LOGIN_SOURCE = "android_app"

const SOCIAL_LOGIN_FLOW_STORAGE_KEY = "tellingPapa:social-login-flow"
const SOCIAL_LOGIN_STATE_PREFIX = "tp1."
const ANDROID_APP_PACKAGE_NAME = "com.tbros.tellingpapa"

const getCurrentWindow = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window
}

const normalizeCallback = (callback) => {
  if (typeof callback !== "string") {
    return null
  }

  const normalizedCallback = callback.trim()
  if (!normalizedCallback || normalizedCallback === "null") {
    return null
  }

  return normalizedCallback
}

const normalizeSource = (source) => {
  if (source !== ANDROID_APP_LOGIN_SOURCE) {
    return null
  }

  return source
}

const normalizeNonce = (nonce) => {
  if (typeof nonce !== "string") {
    return null
  }

  const normalizedNonce = nonce.trim()
  if (!normalizedNonce) {
    return null
  }

  return normalizedNonce
}

const getFlowStorage = () => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow || !currentWindow.sessionStorage) {
    return null
  }

  return currentWindow.sessionStorage
}

const readSocialLoginFlows = () => {
  const storage = getFlowStorage()
  if (!storage) {
    return {}
  }

  try {
    const rawFlows = storage.getItem(SOCIAL_LOGIN_FLOW_STORAGE_KEY)
    if (!rawFlows) {
      return {}
    }

    return JSON.parse(rawFlows)
  } catch (error) {
    return {}
  }
}

const writeSocialLoginFlows = (flows) => {
  const storage = getFlowStorage()
  if (!storage) {
    return
  }

  try {
    if (!Object.keys(flows).length) {
      storage.removeItem(SOCIAL_LOGIN_FLOW_STORAGE_KEY)
      return
    }

    storage.setItem(SOCIAL_LOGIN_FLOW_STORAGE_KEY, JSON.stringify(flows))
  } catch (error) {
  }
}

const hasNativeAppBridge = () => {
  const currentWindow = getCurrentWindow()
  return Boolean(currentWindow && currentWindow.TellingPapaAppBridge)
}

const createSocialLoginNonce = () => {
  const currentWindow = getCurrentWindow()
  if (currentWindow && currentWindow.crypto && typeof currentWindow.crypto.randomUUID === "function") {
    return currentWindow.crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

const encodeBase64Url = (value) => {
  try {
    if (typeof Buffer !== "undefined") {
      return Buffer.from(value, "utf8")
        .toString("base64")
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/g, "")
    }

    if (typeof TextEncoder !== "function") {
      return null
    }

    const bytes = new TextEncoder().encode(value)
    let binaryString = ""
    bytes.forEach((byte) => {
      binaryString += String.fromCharCode(byte)
    })

    const currentWindow = getCurrentWindow()
    const encode = currentWindow && typeof currentWindow.btoa === "function"
      ? currentWindow.btoa.bind(currentWindow)
      : typeof btoa === "function"
        ? btoa
        : null

    if (!encode) {
      return null
    }

    return encode(binaryString)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "")
  } catch (error) {
    return null
  }
}

const decodeBase64Url = (value) => {
  try {
    const normalizedValue = value
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(value.length + ((4 - value.length % 4) % 4), "=")

    if (typeof Buffer !== "undefined") {
      return Buffer.from(normalizedValue, "base64").toString("utf8")
    }

    const currentWindow = getCurrentWindow()
    const decode = currentWindow && typeof currentWindow.atob === "function"
      ? currentWindow.atob.bind(currentWindow)
      : typeof atob === "function"
        ? atob
        : null

    if (!decode || typeof TextDecoder !== "function") {
      return null
    }

    const binaryString = decode(normalizedValue)
    const bytes = Uint8Array.from(binaryString, (character) => character.charCodeAt(0))
    return new TextDecoder().decode(bytes)
  } catch (error) {
    return null
  }
}

export const decodeSocialLoginState = (state) => {
  if (typeof state !== "string") {
    return null
  }

  const normalizedState = state.trim()
  if (!normalizedState || !normalizedState.startsWith(SOCIAL_LOGIN_STATE_PREFIX)) {
    return null
  }

  const encodedPayload = normalizedState.slice(SOCIAL_LOGIN_STATE_PREFIX.length)
  const decodedPayload = decodeBase64Url(encodedPayload)
  if (!decodedPayload) {
    return null
  }

  try {
    const payload = JSON.parse(decodedPayload)
    const nonce = normalizeNonce(payload.nonce)
    if (!nonce) {
      return null
    }

    return {
      nonce,
      source: normalizeSource(payload.source),
      successCallback: normalizeCallback(payload.successCallback),
      failCallback: normalizeCallback(payload.failCallback),
    }
  } catch (error) {
    return null
  }
}

export const createSocialLoginState = (flow = {}) => {
  const payload = {
    nonce: createSocialLoginNonce(),
    source: normalizeSource(flow.source),
    successCallback: normalizeCallback(flow.successCallback),
    failCallback: normalizeCallback(flow.failCallback),
  }

  const encodedPayload = encodeBase64Url(JSON.stringify(payload))
  if (!encodedPayload) {
    return payload.nonce
  }

  return `${SOCIAL_LOGIN_STATE_PREFIX}${encodedPayload}`
}

export const rememberSocialLoginFlow = (state, flow) => {
  if (!state) {
    return
  }

  const flows = readSocialLoginFlows()
  flows[state] = {
    source: normalizeSource(flow && flow.source),
    successCallback: normalizeCallback(flow && flow.successCallback),
    failCallback: normalizeCallback(flow && flow.failCallback),
  }
  writeSocialLoginFlows(flows)
}

export const consumeSocialLoginFlow = (state) => {
  const decodedFlow = decodeSocialLoginState(state)
  if (decodedFlow) {
    return decodedFlow
  }

  if (!state) {
    return null
  }

  const flows = readSocialLoginFlows()
  const flow = flows[state]
  if (!flow) {
    return null
  }

  delete flows[state]
  writeSocialLoginFlows(flows)

  return {
    nonce: null,
    source: normalizeSource(flow.source),
    successCallback: normalizeCallback(flow.successCallback),
    failCallback: normalizeCallback(flow.failCallback),
  }
}

export const resolveSocialLoginSource = (source) => {
  const normalizedSource = normalizeSource(source)
  if (normalizedSource) {
    return normalizedSource
  }

  if (hasNativeAppBridge()) {
    return ANDROID_APP_LOGIN_SOURCE
  }

  return null
}

export const isAndroidAppLoginSource = (source) => {
  return normalizeSource(source) === ANDROID_APP_LOGIN_SOURCE
}

export const resolveTrustedWebUrl = (targetUrl, defaultPath = "/") => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow) {
    return defaultPath
  }

  const defaultUrl = new URL(defaultPath, currentWindow.location.origin)
  const normalizedTargetUrl = normalizeCallback(targetUrl)
  if (!normalizedTargetUrl) {
    return defaultUrl.toString()
  }

  try {
    const resolvedUrl = new URL(normalizedTargetUrl, currentWindow.location.origin)
    if (resolvedUrl.origin !== currentWindow.location.origin) {
      return defaultUrl.toString()
    }

    return resolvedUrl.toString()
  } catch (error) {
    return defaultUrl.toString()
  }
}

export const buildAndroidAppHandoffUrl = ({ accessToken, targetWebUrl, fallbackUrl }) => {
  if (typeof accessToken !== "string" || !accessToken.trim()) {
    return null
  }

  const resolvedTargetWebUrl = resolveTrustedWebUrl(targetWebUrl, "/")
  const resolvedFallbackUrl = resolveTrustedWebUrl(fallbackUrl || resolvedTargetWebUrl, "/")

  return `intent://auth/handoff?accessToken=${encodeURIComponent(accessToken.trim())}&webUrl=${encodeURIComponent(resolvedTargetWebUrl)}#Intent;scheme=tellingpapa;package=${ANDROID_APP_PACKAGE_NAME};S.browser_fallback_url=${encodeURIComponent(resolvedFallbackUrl)};end`
}
